import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { classifyRequestError } from '../services/api';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import {
  ArrowLeft,
  Phone,
  FileText,
  Calendar,
  Package,
  Plus,
  Loader2,
  Edit,
  Save,
  X,
  Archive,
  Trash2,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ClientPersonalTab } from '../components/ClientPersonalTab';

export const ClientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, language, user } = useAuth();
  const { t } = useTranslation();
  const isConsentAdministrator = user?.role === 'CENTER_ADMIN' || user?.role === 'SUPER_ADMIN';
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const DEFAULT_RGPD_LEGAL_TEXT = `Consiento el tratamiento de mis datos personales por el centro con la finalidad de gestionar mis reservas, servicios y comunicación relacionada con la prestación del servicio.
He sido informado/a de que puedo ejercer mis derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad, así como retirar este consentimiento en cualquier momento.
Los datos se conservarán durante el tiempo necesario para la gestión del servicio y las obligaciones legales aplicables.`;

  const DEFAULT_FICHA_SECTIONS = [
    'personalData',
    'notes',
    'rgpd',
  ];

  const [client, setClient] = useState(null);
  const [center, setCenter] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [sessionPacks, setSessionPacks] = useState([]);
  const [clientPlans, setClientPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [gdprStatus, setGdprStatus] = useState(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [clientEditUnknown, setClientEditUnknown] = useState(false);
  const [clientEditReconciliation, setClientEditReconciliation] = useState('');
  // Round-2 fix for review panel finding (UI-UX reconciling indicator):
  // when UNKNOWN is surfaced by handleSave, the background reconciliation
  // GET runs for up to 20 s. Without a visible indicator the user may
  // click Save / Cancel during the silent check. We toggle this flag
  // in lockstep with the recon GET so the `client-edit-unknown` block
  // can render a `Reconciling…` sub-label for as long as the GET is
  // in flight.
  const [clientEditReconciling, setClientEditReconciling] = useState(false);
  const saveRequestRef = useRef(null);
  // 6.2 (D15): alta de bono con resultado UNKNOWN si el POST /api/session-packs
  // tarda >20 s o sufre abort. Conservamos el modal, el formulario y la key
  // para que el retry explícito re-envíe la MISMA idempotencyKey (D15).
  // No persistimos la key en sessionStorage/localStorage (security §3).
  const [packCreateUnknown, setPackCreateUnknown] = useState(false);
  const [packCreateUnknownMessage, setPackCreateUnknownMessage] = useState('');
  // Round-2 fix for review panel finding (UI-UX reconciling indicator):
  // when UNKNOWN is surfaced by handleCreatePack, the background
  // reconciliation GET runs for up to 20 s. Without a visible indicator
  // the user may click Retry / Cancel during the silent check. We
  // toggle this flag in lockstep with the recon GET so the
  // `pack-create-unknown` block can render a `Reconciling…` sub-label
  // for as long as the GET is in flight.
  const [packCreateReconciling, setPackCreateReconciling] = useState(false);
  const createPackKeyRef = useRef(null);
  // 6.2 §4.2 (R1.3, D2): freeze the exact `/session-packs` POST body
  // alongside `createPackKeyRef` so a Reintentar in UNKNOWN resends the
  // IDENTICAL payload, even if `packFormData` drifts underneath it while
  // the dialog is disabled.
  const createPackPayloadRef = useRef(null);
  const createPackRequestRef = useRef(null);
  const [autoRenewMap, setAutoRenewMap] = useState({});

  // Acceso al área personal (credenciales)
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessEmail, setAccessEmail] = useState('');
  const [accessPassword, setAccessPassword] = useState('');
  const [showAccessPassword, setShowAccessPassword] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  // R1.11 / D11 — `POST /clients/{id}/access` returns an `error_code`
  // on the JSON body for domain-level failures (409 / 404 / 403 / 503).
  // We store the latest code so the access dialog can render the
  // localized message inline (`errors.${accessErrorCode}`). The
  // api.js response interceptor already emits a toast for any
  // resolved code, so the catch block only falls back to the generic
  // toast when the error has no recognized code.
  const [accessErrorCode, setAccessErrorCode] = useState(null);
  // Codes that the new endpoint can return. Listed here so the
  // dialog's inline render, the catch-block fall-through and the
  // omission of the duplicate toast stay consistent. `cross_center_access_denied`
  // is intentionally reused from the existing `errors.*` namespace.
  const ACCESS_ERROR_CODES = [
    'email_already_in_use',
    'invalid_role_for_link',
    'user_already_linked',
    'client_not_found',
    'transactions_unavailable',
    'cross_center_access_denied',
  ];

  // Session pack dialog
  const [showPackDialog, setShowPackDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [deletePackId, setDeletePackId] = useState(null);
  const [deletePackReason, setDeletePackReason] = useState('');
  const [editingPackId, setEditingPackId] = useState(null);
  const [packEditData, setPackEditData] = useState({ totalSessions: 10, remainingSessions: 10, price: '', status: 'PENDING_PAYMENT', paymentMethod: '', expiresAt: '' });
  const defaultProductFormData = {
    productType: 'BONO',
    calendarId: '',
    totalSessions: 10,
    sessionsPerWeek: 2,
    name: '',
    price: '',
    status: 'PENDING_PAYMENT',
    paymentStatus: 'PENDING_PAYMENT',
    paymentMethod: '',
    expiresAt: '',
  };
  const [packFormData, setPackFormData] = useState(defaultProductFormData);

  // Client plan edit / delete state
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planEditData, setPlanEditData] = useState({ name: '', sessionsPerWeek: 2, status: 'ACTIVE', monthlyPrice: 0, paymentMethod: '', endsAt: '' });
  const [showDeletePlanDialog, setShowDeletePlanDialog] = useState(false);
  const [deletePlanId, setDeletePlanId] = useState(null);

  // Session recovery state
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryPlanId, setRecoveryPlanId] = useState(null);
  const [missedSessions, setMissedSessions] = useState([]);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoverUntilByWeek, setRecoverUntilByWeek] = useState({});

  // Booking note inline edit
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const clientRes = await api.get(`/clients/${id}`);
      const centerRes = await api.get(`/centers/${clientRes.data.centerId}`);
      const [bookingsRes, packsRes, calendarsRes, clientPlansRes] = await Promise.all([
        api.get(`/bookings?client_id=${id}`),
        api.get(`/session-packs?client_id=${id}`),
        api.get('/calendars'),
        api.get(`/client-plans?client_id=${id}`),
      ]);

      setClient(clientRes.data);
      setCenter(centerRes.data);
      setFormData(clientRes.data);
      setBookings(bookingsRes.data);
      setSessionPacks(packsRes.data);
      setServices(calendarsRes.data.filter(s => s.active));
      const plans = clientPlansRes.data || [];
      setClientPlans(plans);
      const initialAutoRenew = {};
      plans.forEach(p => { initialAutoRenew[p.id] = p.autoRenew !== false; });
      setAutoRenewMap(initialAutoRenew);
      try {
        const gdprRes = await api.get(`/clients/${id}/gdpr-status`);
        setGdprStatus(gdprRes.data);
      } catch {
        // degrade silently — banner won't show if this fails
      }
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error(t('clientProfile.toast.loadError'));
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => () => {
    const request = saveRequestRef.current;
    saveRequestRef.current = null;
    request?.controller.abort();
    // 6.2 (D15): al desmontar ClientProfile, aborta cualquier POST
    // /api/session-packs pendiente. La cancelación de la espera no cancela
    // el procesamiento backend; el estado UNKNOWN + reconciliación por key
    // son los que protegen frente a doble POST ciego.
    const packRequest = createPackRequestRef.current;
    createPackRequestRef.current = null;
    packRequest?.controller.abort();
  }, []);

  // 6.2 (D15): al cerrar el modal de alta de bono (cancel, ESC, backdrop),
  // limpiamos el estado UNKNOWN y descartamos la key — ya no es la misma
  // operación lógica. El siguiente create generará key nueva.
  useEffect(() => {
    if (!showPackDialog) {
      setPackCreateUnknown(false);
      setPackCreateUnknownMessage('');
      createPackKeyRef.current = null;
      createPackPayloadRef.current = null;
    }
  }, [showPackDialog]);

  const saveNote = async (bookingId) => {
    try {
      await api.patch(`/bookings/${bookingId}/internal-note`, { note: noteText });
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, internalNote: noteText } : b));
      setEditingNoteId(null);
    } catch {
      toast.error(t('success.planner.noteSaveError'));
    }
  };

  const updateNested = (path, value) => {
    setFormData(prev => {
      const next = { ...prev };
      let cur = next;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        cur[key] = { ...(cur[key] || {}) };
        cur = cur[key];
      }
      cur[path[path.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (packFormData.status === 'ACTIVE' && !packFormData.paymentMethod) {
      toast.error(t('sessionPacks.selectPaymentMethod') || 'Select a payment method');
      return;
    }

    const payload = {
      fullName: formData.fullName,
      surname: formData.surname,
      phone: formData.phone,
      email: formData.email,
      birthDate: formData.birthDate,
      professionalSector: formData.professionalSector,
      address: formData.address,
      gdpr: formData.gdpr,
      internalNotes: formData.internalNotes,
      active: formData.active,
      whatsappRemindersEnabled: formData.whatsappRemindersEnabled !== false,
      emailRemindersEnabled: formData.emailRemindersEnabled !== false,
    };
    const controller = new AbortController();
    const request = { controller, timedOut: false };
    saveRequestRef.current = request;
    const timeoutId = setTimeout(() => {
      request.timedOut = true;
      controller.abort();
    }, 20000);
    setClientEditUnknown(false);
    setClientEditReconciliation('');
    setFormLoading(true);
    try {
      await api.put(`/clients/${id}`, payload, { signal: controller.signal });
      setClient(formData);
      setEditing(false);
      toast.success(t('clientProfile.toast.updated'));
    } catch (error) {
      if (saveRequestRef.current === request && (request.timedOut || !error?.response || error?.code === 'ERR_CANCELED')) {
        // Round-1 fix for review panel finding 3 (D10/D11): release
        // `formLoading` and surface UNKNOWN IMMEDIATELY. A stalled
        // reconciliation GET previously kept the spinner running
        // forever and edit mode stayed stuck. The reconciliation GET
        // runs in the background with its own AbortController and
        // 20 s timeout; its job is to refine the message and, on a
        // positive match, commit the reconciled client to state and
        // exit edit mode (round-1 finding 9). Only the no-match branch
        // remains in UNKNOWN with edit mode open for an explicit user
        // decision.
        setClientEditReconciliation('No se pudieron confirmar todos los cambios. Revisa el formulario antes de reintentar.');
        setClientEditUnknown(true);
        if (saveRequestRef.current === request) {
          saveRequestRef.current = null;
          setFormLoading(false);
        }
        const reconController = new AbortController();
        const reconTimeoutId = setTimeout(() => reconController.abort(), 20000);
        const payloadForRecon = payload;
        // Round-2 fix for review panel finding (UI-UX reconciling
        // indicator): flag the in-flight reconciliation GET so the
        // `client-edit-unknown` block can render a `Reconciling…`
        // sub-label while we are still talking to the server. Cleared
        // in `.finally()` so the indicator always disappears when the
        // GET settles — success, failure, or timeout.
        setClientEditReconciling(true);
        api.get(`/clients/${id}`, { signal: reconController.signal })
          .then((response) => {
            const reconciled = response?.data;
            const matches = reconciled && Object.keys(payloadForRecon).every(field => reconciled?.[field] === payloadForRecon[field]);
            if (matches) {
              // Round-1 fix for review panel finding 9: positive
              // reconciliation MUST commit state and exit edit mode
              // — the user must NOT see "we don't know" when we know.
              setClient(reconciled);
              setEditing(false);
              setClientEditUnknown(false);
              setClientEditReconciliation('');
              toast.success(t('clientProfile.toast.updated'));
            } else {
              setClientEditReconciliation('No se pudieron confirmar todos los cambios. Revisa el formulario antes de reintentar.');
            }
          })
          .catch(() => {
            setClientEditReconciliation('No se pudo comprobar si los cambios se aplicaron. Revisa el formulario antes de reintentar.');
          })
          .finally(() => {
            clearTimeout(reconTimeoutId);
            setClientEditReconciling(false);
          });
      } else {
        toast.error(t('clientProfile.toast.updateError'));
      }
    } finally {
      clearTimeout(timeoutId);
      if (saveRequestRef.current === request) {
        saveRequestRef.current = null;
        setFormLoading(false);
      }
    }
  };

  const handleCreatePack = async () => {
    if (!packFormData.calendarId) {
      toast.error(t('sessionPacks.selectService') || 'Select a service');
      return;
    }

    setFormLoading(true);
    try {
      const centerId = user.centerId || client.centerId;

      if (packFormData.productType === 'PLAN') {
        const planRes = await api.post('/plans', {
          centerId,
          name: packFormData.name || 'Plan mensual',
          calendarId: packFormData.calendarId,
          monthlyPrice: Number(packFormData.price),
          sessionsPerWeek: Number(packFormData.sessionsPerWeek),
          active: true,
          allowWeeklyRecovery: true,
        });
        await api.post('/client-plans', {
          centerId,
          clientId: id,
          planId: planRes.data.id,
          startsAt: new Date().toISOString().split('T')[0],
          paymentStatus: packFormData.paymentStatus,
          ...(packFormData.paymentMethod ? { paymentMethod: packFormData.paymentMethod } : {}),
        });
        toast.success(t('success.clientProfile.planMonthlyCreated'));
      } else {
        const isSingle = packFormData.productType === 'SINGLE';
        // 6.2 (D15): generamos UNA key por operación lógica de alta de
        // bono. Se conserva en ref para que el retry explícito re-envíe la
        // MISMA key y el backend pueda reconciliar (replay COMPLETED,
        // 409 PROCESSING, etc.). No se persiste en sessionStorage.
        let idempotencyKey = createPackKeyRef.current;
        let requestBody = createPackPayloadRef.current;
        if (!idempotencyKey) {
          const uuid = (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null);
          idempotencyKey = uuid || `sp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
          createPackKeyRef.current = idempotencyKey;

          // 6.2 §4.2 (R1.3, D2): freeze the exact POST body on the FIRST
          // attempt only. A Reintentar in UNKNOWN reuses this stored
          // object verbatim, even if `packFormData` drifts underneath it
          // while the dialog is disabled.
          requestBody = {
            clientId: id,
            // Backend deriva centerId del JWT (steering/security.md §1).
            // Lo retiramos del body para honrar la regla "tenant del
            // JWT, nunca del cliente". Caveat: SUPER_ADMIN sin centerId
            // en JWT no puede fijar el centro aquí — la selección de
            // centro para SUPER_ADMIN queda fuera del scope de
            // sección 6. /plans y /client-plans (no en scope de esta
            // sección) siguen recibiendo centerId hasta un fix aparte.
            calendarId: packFormData.calendarId,
            totalSessions: isSingle ? 1 : packFormData.totalSessions,
            remainingSessions: isSingle ? 1 : packFormData.totalSessions,
            price: Number(packFormData.price || 0),
            status: packFormData.status,
            idempotencyKey,
            ...(packFormData.paymentMethod ? { paymentMethod: packFormData.paymentMethod } : {}),
            expiresAt: packFormData.expiresAt || null,
          };
          createPackPayloadRef.current = requestBody;
        }

        const controller = new AbortController();
        const request = { controller, timedOut: false };
        createPackRequestRef.current = request;
        // 6.2 (D15): boundary de 20.000 ms — el mismo que usa handleSave.
        // NO introducimos timeout global de Axios (R3.4).
        const timeoutId = setTimeout(() => {
          request.timedOut = true;
          controller.abort();
        }, 20000);

        setPackCreateUnknown(false);
        setPackCreateUnknownMessage('');

        try {
          await api.post('/session-packs', requestBody, { signal: controller.signal });

          createPackKeyRef.current = null;
          createPackPayloadRef.current = null;
          toast.success(t('sessionPacks.created') || 'Session pack created');
        } catch (error) {
          // 6.2 (D15): timeout/abort/transporte SIN respuesta → UNKNOWN.
          // No cerramos el modal ni mostramos toast de error: conservamos
          // el formulario y la key para que el usuario decida.
          const aborted = createPackRequestRef.current === request &&
            (request.timedOut || error?.code === 'ERR_CANCELED' || (!error?.response && controller.signal.aborted));
          if (aborted) {
            // Round-1 fix for review panel finding 4 (D15): we release
            // `formLoading` and surface UNKNOWN IMMEDIATELY. The
            // reconciliation GET runs in the background with its own
            // AbortController and 20 s timeout — its job is to refine
            // the UNKNOWN message and, on a positive match, commit the
            // pack and close the dialog. The user-visible UNKNOWN state
            // is decoupled from the reconciliation GET so it cannot
            // block recovery.
            setPackCreateUnknownMessage('No se pudo confirmar el alta del bono. Revisa el formulario antes de reintentar.');
            setPackCreateUnknown(true);
            if (createPackRequestRef.current === request) {
              createPackRequestRef.current = null;
              setFormLoading(false);
            }
            const reconController = new AbortController();
            const reconTimeoutId = setTimeout(() => reconController.abort(), 20000);
            const clientIdForRecon = id;
            const keyForRecon = idempotencyKey;
            // Round-2 fix for review panel finding (UI-UX reconciling
            // indicator): flag the in-flight reconciliation GET so the
            // `pack-create-unknown` block can render a `Reconciling…`
            // sub-label while we are still talking to the server.
            // Cleared in `.finally()` so the indicator always
            // disappears when the GET settles — success, failure, or
            // timeout.
            setPackCreateReconciling(true);
            api.get(`/session-packs?client_id=${clientIdForRecon}`, { signal: reconController.signal })
              .then((response) => {
                const reconciled = response?.data;
                // Round-2 fix for review panel finding (round-1
                // regression on the session-pack matcher): packs are
                // reconciled by `idempotencyKey`, mirroring the
                // Planner.js:946 booking matcher. The backend now
                // persists `idempotencyKey` on the pack doc (mirroring
                // the booking path at server.py:5834) and exposes it
                // on SessionPackResponse. The round-1 swap to
                // `p.operationId` compared the server UUID for the
                // session_pack_operations row against the client UUID
                // and could never reconcile; this restores the parity
                // with Planner.js.
                const matches = Array.isArray(reconciled)
                  ? reconciled.find((p) => p && p.idempotencyKey && p.idempotencyKey === keyForRecon)
                  : null;
                if (matches) {
                  setSessionPacks((prev) => {
                    if (prev.some((p) => p && p.id === matches.id)) return prev;
                    return [...prev, matches];
                  });
                  createPackKeyRef.current = null;
                  createPackPayloadRef.current = null;
                  setPackCreateUnknown(false);
                  setPackCreateUnknownMessage('');
                  setShowPackDialog(false);
                  setPackFormData(defaultProductFormData);
                  toast.success(t('sessionPacks.created') || 'Session pack created');
                  fetchData();
                } else {
                  setPackCreateUnknownMessage('No se pudo confirmar el alta del bono. Revisa el formulario antes de reintentar.');
                }
              })
              .catch(() => {
                setPackCreateUnknownMessage('No se pudo comprobar si el bono se creó. Revisa el formulario antes de reintentar.');
              })
              .finally(() => {
                clearTimeout(reconTimeoutId);
                setPackCreateReconciling(false);
              });
          } else {
            // 6.2 §4.4 (R2.1-R2.3, R3.1-R3.3, D3): 4-way routing for a
            // response WITH a body. Reuses `classifyRequestError` rather
            // than inlining a status list (D3). The two idempotency 409s
            // NEVER surface their raw `detail` string — always the
            // friendly, fixed text.
            const detail = error.response?.data?.detail;
            if (error.response?.status === 409 && detail === 'idempotency_key_payload_mismatch') {
              // (1) Payload mismatch on an in-flight/replayed key:
              // discard both refs — the corrected resubmit must be a
              // brand-new operation with a new key (R2.1/R2.2 analogue).
              // Stay in the normal editable dialog state; do NOT enter
              // UNKNOWN.
              toast.error('Esta operación ya se había iniciado con otros datos. Para evitar duplicados, no podemos reutilizarla. Cancela y vuelve a crearla con los datos correctos.');
              createPackKeyRef.current = null;
              createPackPayloadRef.current = null;
            } else if (error.response?.status === 409 && detail === 'idempotency_in_progress') {
              // (2) The previous attempt is still being processed
              // server-side. Conserve both refs so Reintentar resends the
              // identical operation; surface the existing UNKNOWN surface
              // (R2.3).
              setPackCreateUnknownMessage('Estamos comprobando si la operación anterior llegó a completarse. Espera unos segundos y pulsa ‘Reintentar’.');
              setPackCreateUnknown(true);
            } else if (classifyRequestError(error) === 'transient') {
              // (3) 408/429/5xx with a body: transient per steering —
              // conserve refs, enter UNKNOWN with the existing generic
              // message.
              setPackCreateUnknownMessage('No se pudo confirmar el alta del bono. Revisa el formulario antes de reintentar.');
              setPackCreateUnknown(true);
            } else {
              // (4) Definitive 4xx: existing behavior, plus discard both
              // refs (R2.1) so a corrected resubmit sends a NEW key
              // (R2.2).
              toast.error(error.response?.data?.detail || 'Failed to create product');
              createPackKeyRef.current = null;
              createPackPayloadRef.current = null;
            }
          }
          return; // 6.2: el éxito cierra el modal y resetea, pero UNKNOWN/error explícito NO.
        } finally {
          clearTimeout(timeoutId);
          if (createPackRequestRef.current === request) {
            createPackRequestRef.current = null;
            setFormLoading(false);
          }
        }
      }

      setShowPackDialog(false);
      setPackFormData(defaultProductFormData);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create product';
      toast.error(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePack = async () => {
    const reason = (deletePackReason || '').trim();
    if (!deletePackId) return;
    if (!reason) {
      toast.error(t('common.reasonRequired') || 'Motivo obligatorio');
      return;
    }
    setFormLoading(true);
    try {
      await api.delete(`/session-packs/${deletePackId}`, { data: { reason } });
      toast.success(t('sessionPacks.deleted'));
      setShowDeleteDialog(false);
      setDeletePackId(null);
      setDeletePackReason('');
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        setShowDeleteDialog(false);
        setDeletePackReason('');
        setDeletePackId(deletePackId);
        setShowDeactivateDialog(true);
      } else {
        const msg = error.response?.data?.detail || 'Failed to delete session pack';
        toast.error(msg);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivatePack = async () => {
    console.log('handleDeactivatePack called, deletePackId:', deletePackId);
    if (!deletePackId) {
      toast.error(t('success.clientProfile.packNotIdentified'));
      return;
    }
    setFormLoading(true);
    try {
      await api.patch(`/session-packs/${deletePackId}/deactivate`);
      toast.success(t('sessionPacks.deactivated') || 'Bono desactivado');
      setShowDeactivateDialog(false);
      setDeletePackId(null);
      fetchData();
    } catch (error) {
      console.error('Deactivate error:', error);
      const msg = error.response?.data?.detail || 'Failed to deactivate session pack';
      toast.error(msg);
    } finally {
      setFormLoading(false);
    }
  };

const handleUpdatePack = async (packId) => {
    setFormLoading(true);
    try {
      await api.put(`/session-packs/${packId}`, {
        totalSessions: Number(packEditData.totalSessions),
        remainingSessions: Number(packEditData.remainingSessions),
        price: Number(packEditData.price || 0),
        status: packEditData.status,
        paymentMethod: packEditData.paymentMethod || null,
        expiresAt: packEditData.expiresAt || null,
      });
      toast.success(t('sessionPacks.updated') || 'Session pack updated');
      setEditingPackId(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update session pack';
      toast.error(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdatePlan = async (cp) => {
    setFormLoading(true);
    try {
      await Promise.all([
        api.put(`/plans/${cp.planId}`, {
          name: planEditData.name || undefined,
          sessionsPerWeek: Number(planEditData.sessionsPerWeek),
          monthlyPrice: Number(planEditData.monthlyPrice),
        }),
        api.put(`/client-plans/${cp.id}`, {
          status: planEditData.status,
          ...(planEditData.paymentMethod ? { paymentMethod: planEditData.paymentMethod } : {}),
          ...(planEditData.endsAt ? { endsAt: planEditData.endsAt } : {}),
        }),
      ]);
      toast.success(t('success.clientProfile.planUpdated'));
      setEditingPlanId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar plan');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    if (!deletePlanId) return;
    setFormLoading(true);
    try {
      await api.post(`/client-plans/${deletePlanId}/cancel`);
      toast.success(t('success.clientProfile.planCancelled'));
      setShowDeletePlanDialog(false);
      setDeletePlanId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar plan');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleAutoRenew = async (planId) => {
    const current = autoRenewMap[planId] !== false;
    const newValue = !current;
    setAutoRenewMap(prev => ({ ...prev, [planId]: newValue }));
    try {
      await api.patch(`/client-plans/${planId}`, { autoRenew: newValue });
    } catch (error) {
      setAutoRenewMap(prev => ({ ...prev, [planId]: current }));
      toast.error(error.response?.data?.detail || 'Error al actualizar renovación automática');
      fetchData();
    }
  };

  const handleViewMissedSessions = async (planId) => {
    setRecoveryLoading(true);
    try {
      const res = await api.get(`/client-plans/${planId}/missed-sessions`);
      const weeks = res.data || [];
      if (weeks.length === 0) {
        toast.info('No hay sesiones recuperables');
        return;
      }
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30);
      const defaultStr = defaultDate.toISOString().split('T')[0];
      const initMap = {};
      weeks.forEach(w => { initMap[w.week] = defaultStr; });
      setRecoverUntilByWeek(initMap);
      setMissedSessions(weeks);
      setRecoveryPlanId(planId);
      setRecoveryOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar sesiones');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleRecoverSession = async (planId, week, recoverUntil) => {
    if (!recoverUntil) {
      toast.error(t('success.clientProfile.recoveryDateRequired'));
      return;
    }
    setRecoveryLoading(true);
    try {
      await api.post(`/client-plans/${planId}/recover-session`, { week, recoverUntil });
      toast.success(t('success.clientProfile.recoveryPackCreated'));
      setRecoveryOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear bono de recuperación');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'ATTENDED': return 'status-attended';
      case 'NO_SHOW': return 'status-noshow';
      default: return 'status-reserved';
    }
  };

  const handleArchiveClient = async () => {
    setFormLoading(true);
    try {
      await api.delete(`/clients/${id}`);
      toast.success(t('clients.archived'));
      navigate('/clients');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to archive client';
      toast.error(msg);
    } finally {
      setFormLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRestoreClient = async () => {
    setFormLoading(true);
    try {
      await api.put(`/clients/${id}/restore`);
      toast.success(t('success.clients.restored'));
      navigate('/clients');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al restaurar cliente';
      toast.error(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    setFormLoading(true);
    try {
      await api.delete(`/clients/${id}?hard=true`);
      toast.success(t('clients.deleted'));
      navigate('/clients');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete client';
      toast.error(msg);
    } finally {
      setFormLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const openAccessDialog = () => {
    setAccessEmail((client && client.email) ? client.email : '');
    setAccessPassword('');
    setShowAccessPassword(false);
    setAccessErrorCode(null);
    setAccessDialogOpen(true);
  };

  const saveAccess = async () => {
    if (!accessEmail || !accessPassword) {
      toast.error(t('clientProfile.toast.emailPasswordRequired'));
      return;
    }
    setAccessLoading(true);
    setAccessErrorCode(null);
    try {
      if (client && client.userId) {
        // Reset de password (sin cambios — R1.11)
        await api.post(`/users/${client.userId}/set-password`, { password: accessPassword });
        // Best-effort: persistir email en cliente
        try {
          await api.put(`/clients/${id}`, { ...formData, email: accessEmail });
        } catch (e) {
          console.error('saveAccess: email update failed', e);
        }
        toast.success(t('clientProfile.toast.passwordUpdated'));
      } else {
        // R1.11 / D11 — alta / reactivacion: una sola llamada
        // transaccional al endpoint nuevo en vez de la cadena rota
        // `POST /users` + `PUT /clients/{id}`. El endpoint devuelve
        // `{user, client, restoredLink, reason}`; derivamos el toast
        // de exito a partir de `restoredLink` (4.3). Los errors
        // llegan en `data.error_code` y los mapeamos en 4.2 —
        // indicamos `suppressGlobalToast` para que el interceptor
        // de api.js no duplique el toast y sea el catch block el
        // unico que emita el mensaje (asi el dialogo mantiene
        // `accessErrorCode` como fuente de verdad).
        const res = await api.post(
          `/clients/${id}/access`,
          {
            email: accessEmail,
            password: accessPassword,
            fullName: client.fullName,
            language: 'es',
          },
          { suppressGlobalToast: true },
        );
        if (res?.data?.restoredLink === true) {
          toast.success(t('clientProfile.toast.accessReactivated'));
        } else {
          toast.success(t('clientProfile.toast.accessCreated'));
        }
      }
      setAccessDialogOpen(false);
      await fetchData();
    } catch (e) {
      const code = e?.response?.data?.error_code;
      if (code && ACCESS_ERROR_CODES.includes(code)) {
        setAccessErrorCode(code);
        toast.error(t(`errors.${code}`));
      } else {
        setAccessErrorCode(null);
        toast.error(t('clientProfile.toast.accessError'));
      }
    } finally {
      setAccessLoading(false);
    }
  };

  const disableAccess = async () => {
    if (!(client && client.userId)) return;
    setAccessLoading(true);
    try {
      // Desactivar usuario (requiere payload completo)
      const uRes = await api.get(`/users/${client.userId}`);
      const u = uRes?.data;
      await api.put(`/users/${client.userId}`, {
        email: u.email,
        fullName: u.fullName,
        role: u.role,
        centerId: u.centerId,
        language: u.language || 'es',
        active: false,
      });
      toast.success(t('clientProfile.toast.accessDisabled'));
      await fetchData();
    } catch (e) {
      toast.error(t('clientProfile.toast.accessDisableError'));
    } finally {
      setAccessLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="client-profile-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/clients')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        <div className="flex-1">
          {editing ? (
            <Input
              value={formData.fullName}
              onChange={(e) => setFormData(f => ({ ...f, fullName: e.target.value }))}
              className="input-tactical text-xl font-display font-bold"
              data-testid="edit-name-input"
            />
          ) : (
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {client.surname && client.surname.trim()
                ? `${client.fullName} ${client.surname.trim()}`
                : client.fullName}
            </h1>
          )}
        </div>

        {editing ? (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => { setEditing(false); setFormData(client); }}>
              <X className="w-4 h-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleSave} disabled={formLoading}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('common.save')}
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-client-btn">
              <Edit className="w-4 h-4 mr-2" />
              {t('common.edit')}
            </Button>
            {client?.archived ? (
              <Button
                variant="ghost"
                className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-600/10"
                onClick={handleRestoreClient}
                disabled={formLoading}
                data-testid="restore-client-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="text-red-400 hover:text-red-300 hover:bg-red-600/10"
                onClick={handleArchiveClient}
                disabled={formLoading}
                data-testid="archive-client-btn"
              >
                <Archive className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}

        {clientEditUnknown && editing && (
          <div className="basis-full text-sm text-amber-300" data-testid="client-edit-unknown" role="alert" aria-live="polite">
            {clientEditReconciliation}
            {/* Round-2 fix for review panel finding (UI-UX reconciling
                indicator): show a transient `Reconciling…` sub-label
                while the background reconciliation GET is in flight.
                Mirrors the spinner convention used elsewhere in
                ClientProfile (`Loader2` with `animate-spin`). data-testid
                follows the existing pattern for downstream selectors. */}
            {clientEditReconciling && (
              <div
                className="flex items-center gap-2 text-xs text-zinc-400 mt-1"
                data-testid="client-edit-reconciling"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Reconciling…</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Client info */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="p-4 bg-card border border-zinc-800 rounded-sm">
          <div className="flex items-center gap-2 text-zinc-500 text-sm mb-2">
            <Phone className="w-4 h-4" />
            {t('clients.phone')}
          </div>
          {editing ? (
            <Input
              value={formData.phone}
              onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
              className="input-tactical"
              data-testid="edit-phone-input"
            />
          ) : (
            <div className="font-medium">{client.phone || '-'}</div>
          )}
        </div>

        {user?.role !== 'CLIENT' && (
          <div className="md:col-span-2 p-4 bg-card border border-zinc-800 rounded-sm">
            <div className="flex items-center gap-2 text-zinc-500 text-sm mb-2">
              <FileText className="w-4 h-4" />
              {t('clients.notes')}
            </div>
            {editing ? (
              <Textarea
                value={formData.internalNotes}
                onChange={(e) => setFormData(f => ({ ...f, internalNotes: e.target.value }))}
                className="input-tactical"
                rows={2}
                data-testid="edit-notes-input"
              />
            ) : (
              <div className="text-sm">{client.internalNotes || '-'}</div>
            )}
          </div>
        )}

        {editing && (
          <div className="space-y-2 mt-4">
            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <Label>Recordatorios WhatsApp</Label>
              <Switch
                checked={formData.whatsappRemindersEnabled !== false}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, whatsappRemindersEnabled: checked }))}
                data-testid="client-whatsapp-reminders-switch"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm">
              <Label>Recordatorios Email</Label>
              <Switch
                checked={formData.emailRemindersEnabled !== false}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, emailRemindersEnabled: checked }))}
                data-testid="client-email-reminders-switch"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      {/* Acceso al área personal */}
      <div className="p-4 bg-card border border-zinc-800 rounded-sm mb-6" data-testid="client-access-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-zinc-500">{t('clientProfile.access.title')}</div>
            <div className="font-medium">{client.userId ? t('clientProfile.access.enabled') : t('clientProfile.access.disabled')}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {client.userId ? (
              <>
                <Button variant="outline" onClick={openAccessDialog} data-testid="client-reset-password-btn">
                  {t('clientProfile.access.resetPassword')}
                </Button>
                <Button
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-600/10"
                  onClick={disableAccess}
                  disabled={accessLoading}
                  data-testid="client-disable-access-btn"
                >
                  {t('clientProfile.access.disableAccess')}
                </Button>
              </>
            ) : (
              <Button className="btn-primary" onClick={openAccessDialog} data-testid="client-enable-access-btn">
                {t('clientProfile.access.enableAccess')}
              </Button>
            )}
          </div>
        </div>

        <Dialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-wide">
                {client.userId ? t('clientProfile.access.dialogTitleReset') : t('clientProfile.access.dialogTitleEnable')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('clientProfile.access.emailLabel')}</Label>
                <Input
                  value={accessEmail}
                  onChange={(e) => setAccessEmail(e.target.value)}
                  className="input-tactical"
                  placeholder="cliente@email.com"
                  data-testid="client-access-email"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('clientProfile.access.passwordLabel')}</Label>
                <div className="relative">
                  <Input
                    type={showAccessPassword ? 'text' : 'password'}
                    value={accessPassword}
                    onChange={(e) => setAccessPassword(e.target.value)}
                    className="input-tactical pr-10"
                    placeholder="Mín. 6 caracteres"
                    data-testid="client-access-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAccessPassword(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    aria-label="Mostrar/ocultar contraseña"
                  >
                    {showAccessPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                {t('clientProfile.access.adminNote')}
              </div>
              {accessErrorCode && (
                <div
                  className="p-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-sm text-sm"
                  data-testid="client-access-error"
                  role="alert"
                >
                  {t(`errors.${accessErrorCode}`)}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAccessDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button className="btn-primary" onClick={saveAccess} disabled={accessLoading} data-testid="client-save-access-btn">
                  {accessLoading ? t('common.loading') : t('common.save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isConsentAdministrator && gdprStatus !== null && (!gdprStatus.consentGiven && !gdprStatus.centerAccepted || !gdprStatus.privacyAccepted || !gdprStatus.termsAccepted) && (
        <div className="mb-4 px-4 py-3 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-sm text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Consentimientos RGPD pendientes — ve a la sección RGPD de esta ficha para completarlos
        </div>
      )}

      <Tabs defaultValue="packs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ficha" data-testid="ficha-tab">
            <FileText className="w-4 h-4 mr-2" />
            {t('clientProfile.tabs.ficha')}
          </TabsTrigger>
          <TabsTrigger value="packs" data-testid="packs-tab">
            <Package className="w-4 h-4 mr-2" />
            {t('clients.sessionPacks')}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <Calendar className="w-4 h-4 mr-2" />
            {t('clients.bookingHistory')}
          </TabsTrigger>
          {(user?.role === 'CENTER_ADMIN' || user?.role === 'SUPER_ADMIN') && (
            <TabsTrigger value="personal" data-testid="personal-tab">
              <span className="mr-2">👥</span>
              {t('clientProfile.tabs.personal')}
            </TabsTrigger>
          )}
        </TabsList>

        {(user?.role === 'CENTER_ADMIN' || user?.role === 'SUPER_ADMIN') && (
          <TabsContent value="personal" className="min-h-0">
            <ClientPersonalTab clientId={id} api={api} user={user} />
          </TabsContent>
        )}

        {/* Ficha */}
        <TabsContent value="ficha" className="min-h-0">
          <div className="space-y-6 pr-8 pb-72">
            {(() => {
              const sections = (center?.clientFichaSections && center.clientFichaSections.length > 0)
                ? center.clientFichaSections
                : DEFAULT_FICHA_SECTIONS;

              const gdpr = formData.gdpr || {};
              const legalText = gdpr.legalText || center?.rgpdLegalText || DEFAULT_RGPD_LEGAL_TEXT;

              return sections.map((sec) => {
                if (sec === 'personalData') {
                  return (
                    <div key={sec} className="p-4 bg-card border border-zinc-800 rounded-sm">
                      <h2 className="font-display font-semibold uppercase tracking-wide mb-4">{t('clientProfile.sections.personalData')}</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('clientProfile.fields.firstName')}</Label>
                          <Input value={formData.fullName || ''} onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))} disabled={!editing} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('clientProfile.fields.surname')}</Label>
                          <Input value={formData.surname || ''} onChange={(e) => setFormData(p => ({ ...p, surname: e.target.value }))} disabled={!editing} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('clientProfile.fields.phone')}</Label>
                          <Input value={formData.phone || ''} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} disabled={!editing} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('clientProfile.fields.email')}</Label>
                          <Input value={formData.email || ''} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} disabled={!editing} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('clientProfile.fields.birthDate')}</Label>
                          <Input
                            type="date"
                            value={formData.birthDate || ''}
                            onChange={(e) => setFormData(p => ({ ...p, birthDate: e.target.value || null }))}
                            disabled={!editing}
                          />
                          {formData.birthDate && (() => {
                            const today = new Date();
                            const birth = new Date(formData.birthDate);
                            let age = today.getFullYear() - birth.getFullYear();
                            const m = today.getMonth() - birth.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                            return <div className="text-xs text-zinc-400 mt-1">{age} años</div>;
                          })()}
                        </div>
                        <div className="space-y-2">
                          <Label>{t('clientProfile.fields.professionalSector')}</Label>
                          <Input value={formData.professionalSector || ''} onChange={(e) => setFormData(p => ({ ...p, professionalSector: e.target.value }))} disabled={!editing} />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>{t('clientProfile.fields.address')}</Label>
                          <Input value={formData.address || ''} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} disabled={!editing} />
                        </div>
                      </div>
                    </div>
                  );
                }

                if (sec === 'notes') {
                  if (user?.role === 'CLIENT') return null;
                  return (
                    <div key={sec} className="p-4 bg-card border border-zinc-800 rounded-sm">
                      <h2 className="font-display font-semibold uppercase tracking-wide mb-4">{t('clients.notes')}</h2>
                      {editing ? (
                        <Textarea
                          value={formData.internalNotes || ''}
                          onChange={(e) => setFormData(p => ({ ...p, internalNotes: e.target.value }))}
                          className="input-tactical w-full"
                          rows={3}
                        />
                      ) : (
                        <div className="text-sm">
                          {formData.internalNotes
                            ? formData.internalNotes
                            : <span className="text-zinc-500">Sin notas</span>
                          }
                        </div>
                      )}
                    </div>
                  );
                }

                if (sec === 'rgpd') {
                  const consentGiven = gdprStatus ? gdprStatus.consentGiven : Boolean(gdpr.consentGiven);
                  const centerAccepted = Boolean(gdprStatus?.centerAccepted);
                  const consentSatisfied = consentGiven || centerAccepted;
                  const consentDate = consentGiven
                    ? (gdprStatus?.consentDate || gdpr.consentDate)
                    : gdprStatus?.centerAcceptedAt;
                  const privacyAccepted = Boolean(gdprStatus?.privacyAccepted);
                  const privacyAcceptedAt = gdprStatus?.privacyAcceptedAt;
                  const termsAccepted = Boolean(gdprStatus?.termsAccepted);
                  const termsAcceptedAt = gdprStatus?.termsAcceptedAt;

                  const handleConsentGiven = async () => {
                    if (consentSaving) return;
                    setConsentSaving(true);
                    try {
                      const res = await api.post(`/clients/${id}/consent-given`);
                      setGdprStatus(res.data);
                      setFormData(previous => ({
                        ...previous,
                        gdpr: {
                          ...(previous.gdpr || {}),
                          consentGiven: res.data.consentGiven,
                          consentDate: res.data.consentDate,
                        },
                      }));
                      toast.success(t('success.clientProfile.consentRecorded'));
                    } catch {
                      toast.error(t('success.clientProfile.consentError'));
                    } finally {
                      setConsentSaving(false);
                    }
                  };

                  const handlePrivacyAccept = async () => {
                    try {
                      const res = await api.post(`/clients/${id}/privacy-accept`);
                      setGdprStatus(res.data);
                      toast.success(t('success.clientProfile.privacyRegistered'));
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Error al registrar política de privacidad');
                    }
                  };

                  const handleTermsAccept = async () => {
                    try {
                      const res = await api.post(`/clients/${id}/terms-accept`);
                      setGdprStatus(res.data);
                      toast.success(t('success.clientProfile.termsRegistered'));
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Error al registrar términos de uso');
                    }
                  };

                  return (
                    <div key={sec} className="p-4 bg-card border border-zinc-800 rounded-sm">
                      <h2 className="font-display font-semibold uppercase tracking-wide mb-4">{t('clientProfile.sections.rgpd')}</h2>
                      <div className="space-y-4">
                        {isConsentAdministrator && (
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={consentSatisfied}
                              onChange={(e) => { if (e.target.checked) handleConsentGiven(); }}
                              disabled={consentSatisfied || consentSaving}
                            />
                            Consentimiento del cliente
                          </label>
                        )}
                        {consentDate && (
                          <div className="text-xs text-zinc-400">
                            {t('clientProfile.rgpd.dateLabel')} {format(parseISO(consentDate), 'PPpp', { locale })}
                          </div>
                        )}
                        {isConsentAdministrator && (
                          <div className="space-y-2">
                            <Label>{t('clientProfile.fields.legalText')}</Label>
                            <Textarea
                              value={legalText}
                              onChange={(e) => updateNested(['gdpr', 'legalText'], e.target.value)}
                              disabled={!editing}
                              className="min-h-[120px]"
                            />
                          </div>
                        )}

                        {isConsentAdministrator && (
                        <div className="border-t border-zinc-800 pt-4 space-y-3">
                          <div className="text-xs text-zinc-500 font-medium uppercase tracking-wide">
                            Consentimiento documentos legales
                          </div>

                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={privacyAccepted}
                              onChange={(e) => { if (e.target.checked) handlePrivacyAccept(); }}
                              disabled={privacyAccepted}
                            />
                            <span>Acepta Política de Privacidad v1</span>
                            <a
                              href="/legal/privacy_users_v1.md"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-violet-400 hover:text-violet-300 ml-1"
                            >
                              [ver]
                            </a>
                          </label>
                          {privacyAcceptedAt && (
                            <div className="text-xs text-zinc-400 ml-6">
                              Fecha: {format(parseISO(privacyAcceptedAt), 'PPpp', { locale })}
                            </div>
                          )}

                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={termsAccepted}
                              onChange={(e) => { if (e.target.checked) handleTermsAccept(); }}
                              disabled={termsAccepted}
                            />
                            <span>Acepta Términos de Uso APP v1</span>
                            <a
                              href="/legal/terms_app_v1.md"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-violet-400 hover:text-violet-300 ml-1"
                            >
                              [ver]
                            </a>
                          </label>
                          {termsAcceptedAt && (
                            <div className="text-xs text-zinc-400 ml-6">
                              Fecha: {format(parseISO(termsAcceptedAt), 'PPpp', { locale })}
                            </div>
                          )}

                        </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              });
            })()}
          </div>
        </TabsContent>

        {/* Session Packs */}

        <TabsContent value="packs" className="min-h-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold uppercase tracking-wide">
              {t('sessionPacks.title')}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPackDialog(true)}
              data-testid="add-pack-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sessionPacks.addPack')}
            </Button>
          </div>

          {sessionPacks.length === 0 && clientPlans.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              {t('sessionPacks.noPacks')}
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid gap-3">
              {[...clientPlans].sort((a, b) => (a.status === 'ACTIVE' ? -1 : b.status === 'ACTIVE' ? 1 : 0)).map((cp) => {
                const isActive = cp.status === 'ACTIVE';
                const isEditing = editingPlanId === cp.id;
                return (
                  <div
                    key={cp.id}
                    className={`p-4 border rounded-sm ${isActive ? 'border-zinc-800 bg-card' : 'border-zinc-900 bg-zinc-900/30 opacity-50'}`}
                    data-testid={`client-plan-${cp.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-sm bg-blue-500" />
                        <div>
                          <div className="font-medium">{cp.planName || cp.calendarName || 'Plan mensual'}</div>
                          <div className="text-sm text-zinc-500">{cp.calendarName || '—'}</div>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        {isActive ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-blue-500/15 text-blue-400 border border-blue-500/30">
                            Plan mensual
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-zinc-800 text-zinc-400 border border-zinc-700">
                            Inactivo
                          </span>
                        )}
                        <div className="text-xs text-zinc-500">
                          {cp.startsAt ? cp.startsAt.split('T')[0] : '—'}
                          {cp.endsAt ? ` → ${cp.endsAt.split('T')[0]}` : ''}
                        </div>
                        {isActive && (
                          <div className="flex items-center gap-2 mt-1">
                            <Switch
                              id={`auto-renew-${cp.id}`}
                              checked={autoRenewMap[cp.id] !== false}
                              onCheckedChange={() => handleToggleAutoRenew(cp.id)}
                              className="scale-75"
                            />
                            <label htmlFor={`auto-renew-${cp.id}`} className="text-xs text-zinc-400 cursor-pointer select-none">
                              Renovación automática
                            </label>
                          </div>
                        )}
                        <div className="flex gap-1 mt-1">
                          {isActive && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-blue-400 hover:text-blue-300"
                              onClick={() => handleViewMissedSessions(cp.id)}
                              disabled={recoveryLoading}
                              title="Recuperar sesiones"
                              data-testid={`recover-plan-${cp.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              if (isEditing) {
                                setEditingPlanId(null);
                              } else {
                                setEditingPlanId(cp.id);
                                setPlanEditData({
                                  name: cp.planName || '',
                                  sessionsPerWeek: cp.sessionsPerWeek || 2,
                                  status: cp.status,
                                  monthlyPrice: cp.monthlyPrice || 0,
                                  paymentMethod: cp.paymentMethod || '',
                                  endsAt: cp.endsAt ? cp.endsAt.split('T')[0] : '',
                                });
                              }
                            }}
                            data-testid={`edit-plan-${cp.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setDeletePlanId(cp.id);
                              setShowDeletePlanDialog(true);
                            }}
                            data-testid={`delete-plan-${cp.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="mt-3 space-y-3 pt-3 border-t border-zinc-800">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre del plan</Label>
                            <Input
                              value={planEditData.name}
                              onChange={(e) => setPlanEditData(p => ({ ...p, name: e.target.value }))}
                              className="input-tactical text-sm"
                              placeholder="Plan mensual"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Sesiones por semana</Label>
                            <Input
                              type="number"
                              min={1}
                              value={planEditData.sessionsPerWeek}
                              onChange={(e) => setPlanEditData(p => ({ ...p, sessionsPerWeek: e.target.value }))}
                              className="input-tactical text-sm"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Estado</Label>
                          <Select
                            value={planEditData.status}
                            onValueChange={(v) => setPlanEditData(p => ({ ...p, status: v }))}
                          >
                            <SelectTrigger className="input-tactical text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ACTIVE">Activo</SelectItem>
                              <SelectItem value="CANCELLED">Inactivo</SelectItem>
                              <SelectItem value="EXPIRED">Expirado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Precio (€)</Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              value={planEditData.monthlyPrice}
                              onChange={(e) => setPlanEditData(p => ({ ...p, monthlyPrice: e.target.value }))}
                              className="input-tactical text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Método de pago</Label>
                            <Select
                              value={planEditData.paymentMethod}
                              onValueChange={(v) => setPlanEditData(p => ({ ...p, paymentMethod: v }))}
                            >
                              <SelectTrigger className="input-tactical text-sm">
                                <SelectValue placeholder="Seleccionar" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CASH">Efectivo</SelectItem>
                                <SelectItem value="CARD">Tarjeta</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Fecha de caducidad</Label>
                            <Input
                              type="date"
                              value={planEditData.endsAt || ''}
                              onChange={(e) => setPlanEditData(p => ({ ...p, endsAt: e.target.value }))}
                              className="input-tactical text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingPlanId(null)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            className="btn-primary"
                            onClick={() => handleUpdatePlan(cp)}
                            disabled={formLoading}
                          >
                            {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {[...sessionPacks].sort((a, b) => (a.status === 'ACTIVE' ? -1 : b.status === 'ACTIVE' ? 1 : 0)).map((pack) => {
                const calendar = services.find(s => s.id === pack.calendarId);
                const isActive = pack.status === 'ACTIVE';
                const isPending = pack.status === 'PENDING_PAYMENT';
                const isInactive = pack.status === 'INACTIVE';

                return (
                  <div
                    key={pack.id}
                    className={`p-4 border rounded-sm ${isActive ? 'border-zinc-800 bg-card' : isPending ? 'border-amber-600/30 bg-amber-900/10' : isInactive ? 'border-zinc-900 bg-zinc-900/30 opacity-50' : 'border-zinc-900 bg-zinc-900/50 opacity-60'}`}
                    data-testid={`pack-${pack.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: calendar?.color || '#666' }}
                        />
                        <div>
                          <div className="font-medium">{calendar?.name || t('sessionPacks.allServices')}</div>
                          <div className="text-sm text-zinc-500">
                            {pack.remainingSessions}/{pack.totalSessions} {t('sessionPacks.sessionsRemaining')}
                          </div>
                        </div>
                      </div>

                      <div className="text-right flex flex-col items-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            if (editingPackId === pack.id) {
                              setEditingPackId(null);
                            } else {
                              setEditingPackId(pack.id);
                              setPackEditData({ totalSessions: pack.totalSessions, remainingSessions: pack.remainingSessions, price: pack.price == null ? '' : String(pack.price), status: pack.status, paymentMethod: pack.paymentMethod || '', expiresAt: pack.expiresAt ? pack.expiresAt.slice(0, 10) : '' });
                            }
                          }}
                          data-testid={`edit-pack-${pack.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setDeletePackId(pack.id);
                            setDeletePackReason('');
                            setShowDeleteDialog(true);
                          }}
                          data-testid={`delete-pack-${pack.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>

                        <div className="font-mono text-lg">{pack.price}€</div>
                        <div className={`text-xs ${isActive ? 'text-green-400' : isPending ? 'text-amber-400' : 'text-zinc-500'}`}>
                          {isActive ? t('common.active') : isPending ? t('sessionPacks.pendingPayment') : t('common.inactive')}
                        </div>
                      </div>
                    </div>

                    {editingPackId === pack.id && (
                      <div className="mt-3 space-y-3">
                        {/* Fila 1: sesiones y precio */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">{t('sessionPacks.totalSessions') || 'Sesiones totales'}</Label>
                            <Input
                              type="number"
                              min={1}
                              value={packEditData.totalSessions}
                              onChange={(e) => setPackEditData((p) => ({ ...p, totalSessions: e.target.value }))}
                              className="input-tactical text-sm"
                              data-testid={`edit-pack-total-${pack.id}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{t('sessionPacks.remainingSessions') || 'Sesiones restantes'}</Label>
                            <Input
                              type="number"
                              min={0}
                              value={packEditData.remainingSessions}
                              onChange={(e) => setPackEditData((p) => ({ ...p, remainingSessions: e.target.value }))}
                              className="input-tactical text-sm"
                              data-testid={`edit-pack-remaining-${pack.id}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{t('sessionPacks.price') || 'Precio (€)'}</Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              value={packEditData.price}
                              onChange={(e) => setPackEditData((p) => ({ ...p, price: e.target.value }))}
                              onFocus={(e) => e.target.select()}
                              className="input-tactical text-sm"
                              data-testid={`edit-pack-price-${pack.id}`}
                            />
                          </div>
                        </div>

                        {/* Fila 2: estado, método de pago, caducidad y guardar */}
                        <div className="grid grid-cols-4 gap-3 items-end">
                          <div className="space-y-1">
                            <Label className="text-xs">{t('common.status') || 'Estado'}</Label>
                            <Select
                              value={packEditData.status}
                              onValueChange={(value) => setPackEditData((p) => ({ ...p, status: value }))}
                            >
                              <SelectTrigger data-testid={`edit-pack-status-${pack.id}`}>
                                <SelectValue placeholder={t('common.status') || 'Estado'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PENDING_PAYMENT">{t('sessionPacks.pendingPayment') || 'Pendiente de pago'}</SelectItem>
                                <SelectItem value="ACTIVE">{t('common.active') || 'Pagado'}</SelectItem>
                                <SelectItem value="INACTIVE">{t('common.inactive') || 'Inactivo'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">{t('sessionPacks.paymentMethod') || 'Método de pago'}</Label>
                            <Select
                              value={packEditData.paymentMethod || ''}
                              onValueChange={(value) => setPackEditData((p) => ({ ...p, paymentMethod: value }))}
                              disabled={packEditData.status !== 'ACTIVE'}
                            >
                              <SelectTrigger data-testid={`edit-pack-method-${pack.id}`}>
                                <SelectValue placeholder={t('sessionPacks.selectPaymentMethod') || 'Selecciona'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CASH">{t('sessionPacks.cash') || 'Efectivo'}</SelectItem>
                                <SelectItem value="CARD">{t('sessionPacks.card') || 'Tarjeta'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">{t('sessionPacks.expiresAt') || 'Caduca'}</Label>
                            <Input
                              type="date"
                              value={packEditData.expiresAt || ''}
                              onChange={(e) => setPackEditData((p) => ({ ...p, expiresAt: e.target.value }))}
                              className="input-tactical text-sm"
                              data-testid={`edit-pack-expires-${pack.id}`}
                            />
                          </div>

                          <div className="flex justify-end">
                            <Button
                              className="btn-primary"
                              size="sm"
                              onClick={() => handleUpdatePack(pack.id)}
                              disabled={formLoading || (packEditData.status === 'ACTIVE' && !packEditData.paymentMethod)}
                              data-testid={`save-pack-${pack.id}`}
                            >
                            {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                {t('common.save')}
                              </>
                            )}
                          </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </TabsContent>

{/* Booking History */}
        <TabsContent value="history" className="min-h-0">
          <h2 className="font-display font-semibold uppercase tracking-wide mb-4">
            {t('clients.bookingHistory')}
          </h2>

          {bookings.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              {t('planner.noBookings')}
            </div>
          ) : (() => {
            const canEditNote = user?.role === 'CENTER_ADMIN' || user?.role === 'STAFF';
            return (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <div className="space-y-2">
              {bookings.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime)).map((booking) => (
                <div
                  key={booking.id}
                  className="p-3 bg-card border border-zinc-800 rounded-sm"
                  data-testid={`history-booking-${booking.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-2 self-stretch min-h-[40px] rounded-sm flex-shrink-0"
                      style={{ backgroundColor: booking.calendarColor || booking.serviceColor || '#666' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{booking.calendarName || booking.serviceName || "—"}</div>
                      <div className="text-sm text-zinc-500">
                        {format(parseISO(booking.startDateTime), 'EEEE, d MMMM • HH:mm', { locale })}
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${getStatusClass(booking.status)}`}>
                      {t(`bookings.status.${booking.status.toLowerCase()}`)}
                    </div>
                  </div>

                  {(canEditNote || booking.internalNote) && (
                    <div className="mt-2 pl-6">
                      {editingNoteId === booking.id ? (
                        <div className="flex gap-2">
                          <Textarea
                            value={noteText}
                            onChange={e => setNoteText(e.target.value)}
                            rows={2}
                            className="text-sm resize-none flex-1"
                            placeholder="Nota interna..."
                            autoFocus
                          />
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => saveNote(booking.id)}
                              className="p-1 rounded hover:bg-zinc-700 text-green-400 transition-colors"
                              title="Guardar"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingNoteId(null)}
                              className="p-1 rounded hover:bg-zinc-700 text-zinc-500 transition-colors"
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <p className={`text-sm flex-1 ${booking.internalNote ? 'text-zinc-400' : 'text-zinc-600 italic'}`}>
                            {booking.internalNote || (canEditNote ? 'Sin nota' : '')}
                          </p>
                          {canEditNote && (
                            <button
                              onClick={() => { setEditingNoteId(booking.id); setNoteText(booking.internalNote || ''); }}
                              className="p-0.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors flex-shrink-0"
                              title="Editar nota"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Add Pack Dialog */}
      <Dialog open={showPackDialog} onOpenChange={setShowPackDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {t('sessionPacks.addPack')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* 6.2 §4.3 (R1.2, D1): while UNKNOWN, ALL payload fields
                (product type, name, service, sessions, price, payment
                status/method, status, expiry) are non-editable. `disabled`
                on a `<fieldset>` cascades to every descendant
                native/Radix `<button>`/`<input>` in real browsers —
                verified by e2e (§5), not by this jsdom suite: jsdom's
                `.disabled` does not reflect the fieldset cascade. The
                Actions row and the UNKNOWN action row stay OUTSIDE this
                fieldset so Reintentar/Cancelar remain clickable. */}
            <fieldset disabled={packCreateUnknown} className="space-y-4 min-w-0 border-0 p-0 m-0">
            <div className="space-y-2">
              <Label>Tipo de producto *</Label>
              <Select
                value={packFormData.productType}
                onValueChange={(value) => setPackFormData(f => ({ ...f, productType: value }))}
              >
                <SelectTrigger data-testid="product-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BONO">Bono de sesiones</SelectItem>
                  <SelectItem value="PLAN">Plan mensual</SelectItem>
                  <SelectItem value="SINGLE">Sesión suelta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {packFormData.productType === 'PLAN' && (
              <div className="space-y-2">
                <Label>Nombre del plan</Label>
                <Input
                  value={packFormData.name}
                  onChange={(e) => setPackFormData(f => ({ ...f, name: e.target.value }))}
                  className="input-tactical"
                  placeholder="Plan mensual"
                  data-testid="plan-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('services.title')} *</Label>
              <Select
                value={packFormData.calendarId}
                onValueChange={(value) => setPackFormData(f => ({ ...f, calendarId: value }))}
              >
                <SelectTrigger data-testid="pack-service-select">
                  <SelectValue placeholder={t('planner.selectService')} />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: service.color }} />
                        {service.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {packFormData.productType === 'PLAN' ? (
                <div className="space-y-2">
                  <Label>Sesiones por semana</Label>
                  <Input
                    type="number"
                    value={packFormData.sessionsPerWeek}
                    onChange={(e) => setPackFormData(f => ({ ...f, sessionsPerWeek: Number(e.target.value) }))}
                    className="input-tactical"
                    min={1}
                    data-testid="plan-sessions-per-week-input"
                  />
                </div>
              ) : packFormData.productType === 'BONO' ? (
                <div className="space-y-2">
                  <Label>{t('sessionPacks.totalSessions')}</Label>
                  <Input
                    type="number"
                    value={packFormData.totalSessions}
                    onChange={(e) => setPackFormData(f => ({ ...f, totalSessions: Number(e.target.value) }))}
                    className="input-tactical"
                    min={1}
                    data-testid="pack-sessions-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t('sessionPacks.totalSessions')}</Label>
                  <Input type="number" value={1} disabled className="input-tactical" />
                </div>
              )}
              <div className="space-y-2">
                <Label>{t('sessionPacks.price')} (€)</Label>
                <Input
                  type="number"
                  value={packFormData.price}
                  onChange={(e) => setPackFormData(f => ({ ...f, price: e.target.value }))}
                  onFocus={(e) => e.target.select()}
                  className="input-tactical"
                  min={0}
                  step={0.01}
                  data-testid="pack-price-input"
                />
              </div>
            </div>

            {packFormData.productType === 'PLAN' && (
              <>
                <div className="space-y-2">
                  <Label>Estado de pago</Label>
                  <Select
                    value={packFormData.paymentStatus}
                    onValueChange={(value) => setPackFormData(f => ({ ...f, paymentStatus: value }))}
                    data-testid="plan-payment-status-select"
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING_PAYMENT">Pago pendiente</SelectItem>
                      <SelectItem value="ACTIVE">Activo (pagado)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Método de pago</Label>
                  <Select
                    value={packFormData.paymentMethod}
                    onValueChange={(value) => setPackFormData(f => ({ ...f, paymentMethod: value }))}
                    disabled={packFormData.paymentStatus !== 'ACTIVE'}
                    data-testid="plan-payment-method-select"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CASH">Efectivo</SelectItem>
                      <SelectItem value="CARD">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {(packFormData.productType === 'BONO' || packFormData.productType === 'SINGLE') && (
              <>
                <div className="space-y-2">
                  <Label>{t('sessionPacks.status') || 'Status'}</Label>
                  <Select
                    value={packFormData.status}
                    onValueChange={(value) => setPackFormData(f => ({ ...f, status: value }))}
                  >
                    <SelectTrigger data-testid="pack-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING_PAYMENT">{t('sessionPacks.pendingPayment') || 'Pending Payment'}</SelectItem>
                      <SelectItem value="ACTIVE">{t('common.active') || 'Active (Paid)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('sessionPacks.paymentMethod') || 'Payment Method'}</Label>
                  <Select
                    value={packFormData.paymentMethod}
                    onValueChange={(value) => setPackFormData(f => ({ ...f, paymentMethod: value }))}
                    disabled={packFormData.status !== 'ACTIVE'}
                  >
                    <SelectTrigger data-testid="pack-payment-method-select">
                      <SelectValue placeholder={t('sessionPacks.selectPaymentMethod') || 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CASH">{t('sessionPacks.cash') || 'Efectivo'}</SelectItem>
                      <SelectItem value="CARD">{t('sessionPacks.card') || 'Tarjeta'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('sessionPacks.expiresAt') || 'Fecha de caducidad (opcional)'}</Label>
                  <Input
                    type="date"
                    value={packFormData.expiresAt || ''}
                    onChange={(e) => setPackFormData(f => ({ ...f, expiresAt: e.target.value }))}
                    className="input-tactical"
                    data-testid="pack-expires-input"
                  />
                </div>
              </>
            )}
            </fieldset>


            {/* Actions — se oculta cuando el bloque UNKNOWN (D15) está visible
                para evitar dos filas de acciones primarias con el mismo handler. */}
            {!packCreateUnknown && (
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPackDialog(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  className="btn-primary"
                  onClick={handleCreatePack}
                  disabled={formLoading}
                  data-testid="save-pack-btn"
                >
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.create')}
                </Button>
              </div>
            )}

            {/* 6.2 (D15): estado UNKNOWN tras timeout/abort en el alta de
                bono. El modal se conserva con el formulario intacto; el
                usuario decide si reintentar (misma key, replay) o cancelar
                (descarta key). */}
            {packCreateUnknown && (
              <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="pack-create-unknown" role="alert" aria-live="polite">
                <div className="text-sm text-amber-300">
                  {packCreateUnknownMessage || 'No se pudo confirmar el alta del bono.'}
                </div>
                {/* Round-2 fix for review panel finding (UI-UX reconciling
                    indicator): show a transient `Reconciling…` sub-label
                    while the background reconciliation GET is in flight.
                    Mirrors the spinner convention used elsewhere in
                    ClientProfile (`Loader2` with `animate-spin`). data-testid
                    follows the existing pattern for downstream selectors. */}
                {packCreateReconciling && (
                  <div
                    className="flex items-center gap-2 text-xs text-zinc-400"
                    data-testid="pack-create-reconciling"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Reconciling…</span>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowPackDialog(false)}
                    data-testid="pack-unknown-close-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="btn-primary"
                    onClick={handleCreatePack}
                    disabled={formLoading}
                    data-testid="pack-unknown-retry-btn"
                  >
                    Reintentar (misma operación)
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sessionPacks.confirmDelete') || 'Borrar bono'}</DialogTitle>
            <div className="text-sm text-zinc-500">
              {t('sessionPacks.deleteRequiresReason') || 'Debes indicar un motivo. Se registrará en auditoría.'}
            </div>
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t('common.reason') || 'Motivo'}</Label>
            <textarea
              className="w-full min-h-[90px] bg-zinc-950 border border-zinc-800 rounded-sm p-2 text-sm"
              value={deletePackReason}
              onChange={(e) => setDeletePackReason(e.target.value)}
              data-testid="delete-pack-reason"
              placeholder={t('common.reason') || 'Motivo'}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePack}
              disabled={formLoading || !deletePackReason.trim()}
              data-testid="confirm-delete-pack"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('common.delete') || 'Borrar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Plan Dialog */}
      <Dialog open={showDeletePlanDialog} onOpenChange={setShowDeletePlanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar este plan?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">
            El plan pasará a estado cancelado. Las reservas existentes no se verán afectadas.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowDeletePlanDialog(false); setDeletePlanId(null); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelPlan}
              disabled={formLoading}
              data-testid="confirm-delete-plan"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancelar plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery Modal */}
      <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              Recuperar sesiones
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Semanas con sesiones no realizadas. Crea un bono de recuperación para cada una.
            </p>
            {missedSessions.map((item) => (
              <div key={item.week} className="p-3 border border-zinc-800 rounded-sm space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">
                      {item.weekStart
                        ? `${item.weekStart.split('T')[0]} – ${item.weekEnd ? item.weekEnd.split('T')[0] : ''}`
                        : item.week}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {item.recoverable} sesión{item.recoverable !== 1 ? 'es' : ''} recuperable{item.recoverable !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Recuperable hasta</Label>
                    <Input
                      type="date"
                      value={recoverUntilByWeek[item.week] || ''}
                      onChange={(e) =>
                        setRecoverUntilByWeek(prev => ({ ...prev, [item.week]: e.target.value }))
                      }
                      className="input-tactical text-sm"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="btn-primary shrink-0"
                    onClick={() => handleRecoverSession(recoveryPlanId, item.week, recoverUntilByWeek[item.week])}
                    disabled={recoveryLoading || !recoverUntilByWeek[item.week]}
                  >
                    {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear bono'}
                  </Button>
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setRecoveryOpen(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deactivate Dialog (shown when deletion rejected due to associated bookings) */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {t('sessionPacks.deactivateTitle') || '¿Desactivar bono?'}
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-zinc-400">
            {t('sessionPacks.hasBookings') || 'Este bono tiene citas asociadas y no puede eliminarse. ¿Quieres desactivarlo en su lugar?'}
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { setShowDeactivateDialog(false); setDeletePackId(null); }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              className="border-amber-600 text-amber-400 hover:bg-amber-600/10"
              variant="outline"
              onClick={() => {
                console.log('Deactivate button clicked, deletePackId:', deletePackId);
                handleDeactivatePack();
              }}
              disabled={formLoading}
              data-testid="confirm-deactivate-pack"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('sessionPacks.deactivate') || 'Desactivar bono')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
